import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, ShieldCheck, Clock } from "lucide-react";
import { toast } from "sonner";
import { startCheckout } from "../../lib/payments";

const tiers = [
  {
    name: "Starter",
    price: "$299",
    note: "one-time",
    timeline: "1 week",
    tagline: "Get online, fast.",
    features: [
      "1-page professional website",
      "Mobile responsive design",
      "Contact form",
      "Google Business Profile setup",
    ],
    cta: "Start with Starter",
    highlight: false,
  },
  {
    name: "Growth",
    price: "$599",
    note: "one-time",
    timeline: "2 weeks",
    tagline: "Look established & get found.",
    features: [
      "Up to 3-page website",
      "Branding kit (colours & fonts)",
      "Google Business Profile setup",
      "Social media profile setup",
      "1 round of revisions",
    ],
    cta: "Choose Growth",
    highlight: true,
  },
  {
    name: "Premium",
    price: "$999",
    note: "one-time",
    timeline: "1 month",
    tagline: "The full online presence.",
    features: [
      "Everything in Growth",
      "Custom logo design",
      "Basic SEO setup",
      "Social posting plan (1 month)",
      "1 month of support",
    ],
    cta: "Go Premium",
    highlight: false,
  },
];

export default function Pricing() {
  const [loadingId, setLoadingId] = useState(null);

  const buy = async (packageId) => {
    setLoadingId(packageId);
    try {
      await startCheckout(packageId);
    } catch (e) {
      toast.error("Couldn't start checkout. Please try again.");
      setLoadingId(null);
    }
  };

  return (
    <section id="pricing" className="py-24 sm:py-32">
      <div className="mx-auto max-w-6xl px-6 sm:px-12">
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#75C69F]">
            Pricing
          </span>
          <h2 className="mt-4 font-heading text-4xl font-medium tracking-tight text-[#0B281B] sm:text-5xl">
            Honest pricing for the basics.
          </h2>
          <p className="mt-5 text-lg text-[#4A6657]">
            One-time pricing in CAD. No surprise fees. Optional care plans from
            $75/mo for hosting & updates.
          </p>
        </div>

        <div className="mt-16 grid items-center gap-6 lg:grid-cols-3">
          {tiers.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`pricing-card-${t.name.toLowerCase()}`}
              className={`relative rounded-3xl p-8 ${
                t.highlight
                  ? "bg-[#0B281B] text-white lg:scale-105 shadow-[0_20px_60px_rgba(11,40,27,0.25)]"
                  : "bg-white border border-[#DCECE3]"
              }`}
            >
              {t.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#98D8BA] px-4 py-1 text-xs font-bold uppercase tracking-wider text-[#0B281B]">
                  Most popular
                </span>
              )}
              {t.timeline && (
                <span
                  data-testid={`pricing-timeline-${t.name.toLowerCase()}`}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${
                    t.highlight
                      ? "bg-[#98D8BA]/20 text-[#98D8BA]"
                      : "bg-[#F0F5F2] text-[#0B281B]"
                  }`}
                >
                  <Clock className="h-3.5 w-3.5" /> {t.timeline} delivery
                </span>
              )}
              <h3
                className={`mt-4 font-heading text-xl font-medium ${
                  t.highlight ? "text-[#98D8BA]" : "text-[#0B281B]"
                }`}
              >
                {t.name}
              </h3>
              <p
                className={`mt-1 text-sm ${
                  t.highlight ? "text-white/70" : "text-[#4A6657]"
                }`}
              >
                {t.tagline}
              </p>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-heading text-5xl font-bold">{t.price}</span>
                <span
                  className={`text-sm ${
                    t.highlight ? "text-white/60" : "text-[#4A6657]"
                  }`}
                >
                  {t.note}
                </span>
              </div>

              <button
                onClick={() => buy(t.name.toLowerCase())}
                disabled={loadingId === t.name.toLowerCase()}
                data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                className={`mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 font-medium transition-colors disabled:opacity-70 ${
                  t.highlight
                    ? "bg-[#98D8BA] text-[#0B281B] hover:bg-[#80C5A5]"
                    : "bg-[#0B281B] text-white hover:bg-[#1A3F2C]"
                }`}
              >
                {loadingId === t.name.toLowerCase() && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                {loadingId === t.name.toLowerCase() ? "Redirecting…" : t.cta}
              </button>

              <ul className="mt-8 space-y-3">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <span
                      className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${
                        t.highlight ? "bg-[#98D8BA]" : "bg-[#F0F5F2]"
                      }`}
                    >
                      <Check className="h-3 w-3 text-[#0B281B]" strokeWidth={3} />
                    </span>
                    <span className={t.highlight ? "text-white/90" : "text-[#4A6657]"}>
                      {f}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Care plan */}
        <div
          data-testid="care-plan-banner"
          className="mt-8 flex flex-col items-center justify-between gap-6 rounded-3xl border border-[#DCECE3] bg-[#F0F5F2] p-8 sm:flex-row"
        >
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-[#98D8BA]">
              <ShieldCheck className="h-6 w-6 text-[#0B281B]" strokeWidth={1.75} />
            </span>
            <div>
              <h3 className="font-heading text-2xl font-medium text-[#0B281B]">
                Care Plan — $75<span className="text-base font-normal text-[#4A6657]">/mo</span>
              </h3>
              <p className="mt-1 max-w-md text-sm text-[#4A6657]">
                Hosting, updates, backups and priority support. A true monthly
                subscription — cancel anytime.
              </p>
            </div>
          </div>
          <button
            onClick={() => buy("care")}
            disabled={loadingId === "care"}
            data-testid="care-plan-cta"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0B281B] px-8 py-4 font-medium text-white transition-colors hover:bg-[#1A3F2C] disabled:opacity-70"
          >
            {loadingId === "care" && <Loader2 className="h-4 w-4 animate-spin" />}
            {loadingId === "care" ? "Redirecting…" : "Start Care Plan"}
          </button>
        </div>
      </div>
    </section>
  );
}
